// Notiserna i webbläsaren: Web Push med tjänstearbetaren. Appen ber om tillåtelse en gång,
// efter inloggningen, med en kort förklaring. På iPhone fungerar push bara när appen är
// installerad på hemskärmen.

import { api } from "./api.ts";

export type Notislage =
  /** Webbläsaren kan inte ta emot push. */
  | "kan-inte"
  /** iPhone eller iPad i Safari: appen måste läggas på hemskärmen först. */
  | "hemskarm"
  /** Ägaren har sagt nej i webbläsaren. */
  | "nekad"
  | "av"
  | "pa";

export function arIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function installerad(): boolean {
  return (
    matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** VAPID-nyckeln i base64url till byte. */
function nyckel(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const text = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const ut = new Uint8Array(new ArrayBuffer(text.length));
  for (let i = 0; i < text.length; i++) ut[i] = text.charCodeAt(i);
  return ut;
}

async function prenumeration(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function notislage(): Promise<Notislage> {
  if (arIos() && !installerad()) return "hemskarm";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window))
    return "kan-inte";
  if (Notification.permission === "denied") return "nekad";
  return Notification.permission === "granted" && (await prenumeration()) ? "pa" : "av";
}

/** Ber om tillåtelse och lämnar prenumerationen till molnet. */
export async function slaPaNotiser(vapidPublik: string): Promise<Notislage> {
  const svar = await Notification.requestPermission();
  if (svar !== "granted") return svar === "denied" ? "nekad" : "av";
  const reg = await navigator.serviceWorker.ready;
  const p =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: nyckel(vapidPublik),
    }));
  await api.prenumerera(p.toJSON());
  return "pa";
}

export async function slaAvNotiser(): Promise<void> {
  const p = await prenumeration();
  if (!p) return;
  await api.avprenumerera(p.endpoint).catch(() => {});
  await p.unsubscribe();
}

const FRAGAT = "batkoll.notisfraga";

/** Frågan om notiser visas en gång. */
export function harFragat(): boolean {
  try {
    return localStorage.getItem(FRAGAT) !== null;
  } catch {
    return false;
  }
}

export function markeraFragat(): void {
  try {
    localStorage.setItem(FRAGAT, new Date().toISOString());
  } catch {}
}
